import { ref, reactive, computed, watch } from 'vue'
import type { ECGlobal, ECGlobalRequest } from '@/types/maquette'
import { useApi } from '@/composables/useApi'
import { toast } from 'vue3-toastify'
import { extractApiErrorMessage } from '@/utils/apiError'

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
  total?: number;
  current_page?: number;
  per_page?: number;
}

interface ECGlobalState {
  ecGlobals: ECGlobal[];
  ecGlobal: ECGlobal | null;
  loading: boolean;
  errors: Record<string, string[]>;
  filters: {
    search: string;
    matiere_id: number | null;
    is_active: 'Tous' | 'Actifs' | 'Inactifs';
    page: number;
    per_page: number;
  };
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
  };
}

export function useECGlobals() {
  const { apiData } = useApi()
  
  const state = reactive<ECGlobalState>({
    ecGlobals: [],
    ecGlobal: null,
    loading: false,
    errors: {},
    filters: {
      search: '',
      matiere_id: null,
      is_active: 'Tous',
      page: 1,
      per_page: 15
    },
    pagination: {
      total: 0,
      current_page: 1,
      per_page: 15
    }
  })

  const formRef = ref()

  const headers = [
    { title: 'Code', align: 'start' as const, key: 'code', sortable: true },
    { title: 'Intitulé', align: 'start' as const, key: 'intitule', sortable: true },
    { title: 'Coefficient', align: 'start' as const, key: 'coefficient', sortable: true },
    { title: 'Matière', align: 'start' as const, key: 'matiere.intitule', sortable: true },
    { title: 'Usage', align: 'start' as const, key: 'usage_count', sortable: true },
    { title: 'Actions', align: 'center' as const, key: 'actions', sortable: false }
  ]

  const rules = {
    intitule: [
      (v: string) => !!v || "L'intitulé est requis",
      (v: string) => v.length <= 255 || "L'intitulé ne doit pas dépasser 255 caractères"
    ],
    coefficient: [
      (v: number) => !!v || 'Le coefficient est requis',
      (v: number) => v > 0 || 'Le coefficient doit être supérieur à 0'
    ],
    matiere_id: [
      (v: number) => !!v || 'La matière est requise'
    ]
  }

  const loadingState = ref(false)

  // Filtrage côté client comme dans les autres pages
  const filteredECGlobals = computed(() => {
    return state.ecGlobals.filter(ec => {
      const searchTerm = (state.filters.search || '').toLowerCase().trim()
      const matchSearch = !searchTerm ||
        ec.code?.toLowerCase().includes(searchTerm) ||
        ec.intitule?.toLowerCase().includes(searchTerm)
      
      const matchStatus = state.filters.is_active === 'Tous' ||
        (state.filters.is_active === 'Actifs' && ec.is_active) ||
        (state.filters.is_active === 'Inactifs' && !ec.is_active)
      
      const matchMatiere = !state.filters.matiere_id || ec.matiere_id === state.filters.matiere_id
      
      return matchSearch && matchStatus && matchMatiere
    })
  })

  const ecGlobalForm = ref<ECGlobalRequest>({
    intitule: '',
    code: '',
    code_manual: false, // Par défaut, le code est auto-généré par le backend
    coefficient: 1,
    matiere_id: null, // Maintenant le type accepte null
    volume_cm: 0,
    volume_td: 0,
    volume_tp: 0,
    volume_tpe: 0,
    volume_vht: 0,
    volume_effectif: 0,
    is_active: true
  })

  function openDialog(type: 'create' | 'edit' = 'create', item?: ECGlobal) {
    if (type === 'edit' && item) {
      ecGlobalForm.value = {
        id: item.id,
        code: item.code,
        code_manual: !!item.code, // Si il y a un code, c'est qu'il a été saisi manuellement
        intitule: item.intitule,
        coefficient: item.coefficient,
        matiere_id: item.matiere_id,
        volume_cm: item.volume_cm,
        volume_td: item.volume_td,
        volume_tp: item.volume_tp,
        volume_tpe: item.volume_tpe,
        volume_vht: item.volume_vht,
        volume_effectif: item.volume_effectif,
        is_active: item.is_active
      }
    } else {
      ecGlobalForm.value = {
        intitule: '',
        code: '',
        code_manual: false,
        coefficient: 1,
        matiere_id: null, // Maintenant le type accepte null
        volume_cm: 0,
        volume_td: 0,
        volume_tp: 0,
        volume_tpe: 0,
        volume_vht: 0,
        volume_effectif: 0,
        is_active: true
      }
    }
    state.errors = {}
  }

  async function fetchECGlobals() {
    try {
      loadingState.value = true
      const params = new URLSearchParams()
      
      // Ne plus envoyer le filtre search au backend - filtrage côté client
      if (state.filters.matiere_id) params.append('matiere_id', state.filters.matiere_id.toString())
      if (state.filters.is_active !== 'Tous') params.append('is_active', (state.filters.is_active === 'Actifs').toString())
      // Toujours charger tous les éléments pour paginer côté client (comme UEs)
      params.append('per_page', 'all')
      
      const res = await apiData<ApiResponse<any>>(`/ec-globals?${params.toString()}`)
      
      if (res?.success) {
        if (Array.isArray(res.data)) {
          state.ecGlobals = res.data
          state.pagination.total = res.data.length
          state.pagination.current_page = 1
          state.pagination.per_page = state.filters.per_page
        } else if (res.data && typeof res.data === 'object' && 'data' in res.data) {
          // Par sécurité, si le backend renvoie tout de même un objet paginé
          const list = res.data.data || []
          state.ecGlobals = list
          state.pagination.total = list.length
          state.pagination.current_page = 1
          state.pagination.per_page = state.filters.per_page
        } else {
          state.ecGlobals = []
          state.pagination.total = 0
          state.pagination.current_page = 1
          state.pagination.per_page = state.filters.per_page
        }
      } else {
        state.ecGlobals = []
        state.pagination.total = 0
        state.pagination.current_page = 1
        state.pagination.per_page = state.filters.per_page
      }
    } catch (e) {
      state.ecGlobals = []
      state.pagination.total = 0
      state.pagination.current_page = 1
      state.pagination.per_page = state.filters.per_page
    } finally {
      loadingState.value = false
    }
  }

  // Watchers alignés avec useUEs / useMaquettes (sans refetch sur page/per_page)
  // Le filtre search est géré côté client, pas besoin de watcher

  watch(
    () => state.filters.is_active,
    () => {
      state.filters.page = 1
      fetchECGlobals()
    }
  )

  watch(
    () => state.filters.matiere_id,
    () => {
      state.filters.page = 1
      fetchECGlobals()
    }
  )

  // Nouvelle fonction pour récupérer tous les ECs globaux sans pagination
  async function fetchAllECGlobals() {
    try {
      const params = new URLSearchParams()
      params.append('per_page', 'all') // Récupérer tous les ECs globaux
      
      const res = await apiData<ApiResponse<any>>(`/ec-globals?${params.toString()}`)
      
      if (res?.success) {
        if (res.data && typeof res.data === 'object' && 'data' in res.data) {
          return res.data.data || []
        } else if (Array.isArray(res.data)) {
          return res.data
        }
      }
      return []
    } catch (e) {
      // //console.error('Erreur lors de la récupération de tous les ECs globaux:', e)
      return []
    }
  }

  // Watcher supprimé pour éviter les conflits avec celui de la page principale

  async function fetchECGlobal(id: number) {
    try {
      state.loading = true
      const response = await apiData<ApiResponse<ECGlobal>>(`/ec-globals/${id}`)
      
      if (response?.success) {
        state.ecGlobal = response.data
        return response.data
      }
    } catch (error: any) {
      // //console.error('Erreur lors de la récupération de l\'EC Global:', error)
    } finally {
      state.loading = false
    }
  }

  const formatApiErrorMessage = (res: any, fallback = "Une erreur s'est produite"): string =>
    extractApiErrorMessage(res, fallback)

  async function createECGlobal(data: ECGlobalRequest) {
    try {
      const res = await apiData<ApiResponse<ECGlobal>>('/ec-globals', {
        method: 'POST',
        body: JSON.stringify(data)
      })
      
      if (res.success) {
        await fetchECGlobals()
        return { success: true, message: res.message || 'EC Global créé avec succès' }
      }
      
      if (res.errors && typeof res.errors === 'object') {
        state.errors = res.errors
        const messages = Object.values(res.errors).flat().join('\n')
        return { success: false, message: messages || res.message || 'Erreur de validation' }
      }
      
      const detailed = formatApiErrorMessage(res, res?.message || 'Une erreur s\'est produite')
      return { success: false, message: detailed }
    } catch (err: any) {
      const payload = err?.response?._data || err?.data || err
      if (payload?.errors) {
        state.errors = payload.errors
      }
      const detailed = formatApiErrorMessage(payload, 'Erreur inconnue')
      return { success: false, message: detailed }
    }
  }

  async function updateECGlobal(id: number, data: ECGlobalRequest) {
    try {
      const res = await apiData<ApiResponse<ECGlobal>>(`/ec-globals/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      })
      
      if (res.success) {
        await fetchECGlobals()
        return { success: true, message: res.message || 'EC Global modifié avec succès' }
      }
      
      if (res.errors && typeof res.errors === 'object') {
        state.errors = res.errors
        const messages = Object.values(res.errors).flat().join('\n')
        return { success: false, message: messages || res.message || 'Erreur de validation' }
      }
      
      const detailed = formatApiErrorMessage(res, res?.message || 'Une erreur s\'est produite')
      return { success: false, message: detailed }
    } catch (err: any) {
      const payload = err?.response?._data || err?.data || err
      if (payload?.errors) {
        state.errors = payload.errors
      }
      const detailed = formatApiErrorMessage(payload, 'Erreur inconnue')
      return { success: false, message: detailed }
    }
  }

  async function deleteECGlobal(id: number) {
    try {
      const res = await apiData<ApiResponse<null>>(`/ec-globals/${id}`, {
        method: 'DELETE'
      })
      
      if (res.success) {
        await fetchECGlobals()
        return { success: true, message: res.message || 'EC Global supprimé avec succès' }
      }
      
      const detailed = formatApiErrorMessage(res, res?.message || 'Une erreur s\'est produite lors de la suppression')
      return { success: false, message: detailed }
    } catch (err: any) {
      const payload = err?.response?._data || err?.data || err
      const detailed = formatApiErrorMessage(payload, 'Erreur inconnue')
      return { success: false, message: detailed }
    }
  }

  async function toggleActive(id: number) {
    try {
      const res = await apiData<ApiResponse<ECGlobal>>(`/ec-globals/${id}/toggle-status`, {
        method: 'PATCH'
      })
      
      if (res.success) {
        await fetchECGlobals()
        return { success: true, message: res.message || 'Statut modifié avec succès' }
      }
      
      const detailed = formatApiErrorMessage(res, res?.message || 'Erreur lors de la modification du statut')
      return { success: false, message: detailed }
    } catch (err: any) {
      const payload = err?.response?._data || err?.data || err
      const detailed = formatApiErrorMessage(payload, 'Erreur inconnue')
      return { success: false, message: detailed }
    }
  }

  function resetForm() {
    if (formRef.value) {
      formRef.value.reset()
    }
    state.errors = {}
  }

  function errorsClear() {
    state.errors = {}
  }

  // Fonction pour basculer vers la saisie manuelle
  function toggleManualCode() {
    if (ecGlobalForm.value) {
      ecGlobalForm.value.code_manual = !ecGlobalForm.value.code_manual
      if (!ecGlobalForm.value.code_manual) {
        ecGlobalForm.value.code = '' // Vider le code si on revient en mode auto
      }
    }
  }

  // Fonction pour gérer le changement du switch saisie manuelle
  function onManualCodeChange() {
    if (ecGlobalForm.value && !ecGlobalForm.value.code_manual) {
      ecGlobalForm.value.code = '' // Vider le code si on revient en mode auto
    }
  }

  return {
    ecGlobals: filteredECGlobals, // Retourner le computed filtré côté client
    ecGlobal: computed(() => state.ecGlobal),
    ecGlobalForm,
    loading: loadingState,
    errors: computed(() => state.errors),
    filters: state.filters,
    pagination: computed(() => state.pagination),
    formRef,
    headers,
    rules,
    fetchECGlobals,
    fetchECGlobal,
    createECGlobal,
    updateECGlobal,
    deleteECGlobal,
    toggleActive,
    resetForm,
    errorsClear,
    openDialog,
    fetchAllECGlobals,
    toggleManualCode,
    onManualCodeChange
  }
} 